/**
 * O CNPJ das organizações (#769): a máscara enquanto se digita e a conta do
 * dígito verificador, sem tela. A criação, o detalhe e a lista da gestão usam.
 *
 * Aceita as letras do CNPJ alfanumérico, que a Receita dá às inscrições novas
 * desde julho de 2026 (IN RFB 2.229/2024): os 12 primeiros caracteres podem ser
 * letras ou números, e os 2 últimos, os verificadores, são sempre números. Na
 * conta, cada caractere vale o código dele menos 48 ("A" vale 17).
 *
 * A conta é a mesma do back (`packages/modulos/acesso/src/dominio/cadastro.ts`),
 * repetida aqui só para o aviso aparecer na hora: o servidor confere de novo,
 * e é ele quem diz se outra organização já usa o mesmo CNPJ. Todos os
 * caracteres iguais passam na conta, mas a Receita nunca emite, e são o CNPJ do
 * formulário preenchido às pressas: ficam recusados.
 */

const PESOS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ = /^[0-9A-Z]{12}[0-9]{2}$/;

/** Um dígito verificador, sobre os primeiros caracteres, tantos quantos os pesos. */
function digitoVerificador(cnpj: string, pesos: readonly number[]): number {
  const soma = pesos.reduce((total, peso, i) => total + (cnpj.charCodeAt(i) - 48) * peso, 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/**
 * Um CNPJ inteiro no meio de um texto, com ou sem a máscara: o que se cola de
 * um documento costuma vir com rótulo ("CNPJ: 12.ABC.345/01DE-35"). Antes e
 * depois dele, nada que pudesse ser parte dele.
 */
const CNPJ_NO_TEXTO =
  /(?:^|[^0-9A-Z])([0-9A-Z]{2}[.\s]?[0-9A-Z]{3}[.\s]?[0-9A-Z]{3}[/\s]?[0-9A-Z]{4}[-\s]?[0-9]{2})(?![0-9A-Z])/;

/**
 * Sem a máscara e em maiúsculas: "12.abc.345/01de-35" → "12ABC34501DE35". Colado
 * com rótulo, fica só o CNPJ; sem um CNPJ inteiro, como no meio da digitação,
 * fica o que foi digitado, até 14 caracteres.
 */
export function cnpjSemMascara(texto: string): string {
  const maiusculas = texto.toUpperCase();
  const colado = CNPJ_NO_TEXTO.exec(maiusculas)?.[1];
  return (colado ?? maiusculas).replace(/[^0-9A-Z]/g, '').slice(0, 14);
}

/** "12ABC34501DE35" → "12.ABC.345/01DE-35", também no meio da digitação. */
export function mascararCnpj(texto: string): string {
  const d = cnpjSemMascara(texto);
  let saida = d.slice(0, 2);
  if (d.length > 2) saida += `.${d.slice(2, 5)}`;
  if (d.length > 5) saida += `.${d.slice(5, 8)}`;
  if (d.length > 8) saida += `/${d.slice(8, 12)}`;
  if (d.length > 12) saida += `-${d.slice(12, 14)}`;
  return saida;
}

/** O CNPJ tem o formato certo e os dois verificadores batem? */
export function cnpjValido(texto: string): boolean {
  const cnpj = cnpjSemMascara(texto);
  if (!CNPJ.test(cnpj) || /^(.)\1+$/.test(cnpj)) return false;
  return (
    digitoVerificador(cnpj, PESOS_1) === Number(cnpj[12]) &&
    digitoVerificador(cnpj, PESOS_2) === Number(cnpj[13])
  );
}
